import { cs } from '../../../lib/currency';
import { RETIRED_TOOLS_VISIBLE } from '../../../lib/retiredTools';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { RowAmountFixer } from '../RowAmountFixer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../components/ui/dropdown-menu';
import { Users2, Mail, CreditCard, MoreHorizontal, Send, RefreshCw, Trash2, Phone, CheckCircle, XCircle, AlertCircle, RotateCcw, TicketPercent, RefreshCcw, Pencil } from 'lucide-react';
import { registrationStatusColors, paymentStatusColors } from './constants';
import api from '../../../services/api';
import { toast } from 'sonner';
import ManualDiscountDialog from '../ManualDiscountDialog';
import EditRegistrationDialog from './EditRegistrationDialog';
import NetAmount from '../../shared/NetAmount';

const getPaymentStatusIcon = (status) => {
  switch (status) {
    case 'paid': return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'pending': return <AlertCircle className="h-4 w-4 text-yellow-600" />;
    case 'refunded': return <RefreshCw className="h-4 w-4 text-purple-600" />;
    default: return <XCircle className="h-4 w-4 text-red-600" />;
  }
};

export const RegistrationsTab = ({
  tournament, registrations, filteredRegistrations, teams,
  selectedRegistrations, setSelectedRegistrations,
  isAdmin, isStaff, isCoach, tournamentId,
  ageFilter, setAgeFilter, paymentFilter, setPaymentFilter,
  teamFilter, setTeamFilter, sortField, setSortField, sortOrder, setSortOrder,
  onOpenPaymentDialog, onOpenAddToTeamDialog, onOpenDeleteRegistrationDialog,
  onBulkPaymentReminder, onOpenBulkAddToTeam, onOpenBulkMarkPaid,
  onOpenRefundDialog,
  showArchived, setShowArchived,
  fetchRegistrations
}) => {
  const [discountReg, setDiscountReg] = useState(null);
  const [editReg, setEditReg] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [undoingId, setUndoingId] = useState(null);

  // Undo a manual "Mark as Paid". Only offered for rows paid via an
  // offline method (cash, check, venmo, zelle, bank_transfer, other).
  // Stripe-settled rows must use the Refund flow so the real charge
  // isn't silently orphaned.
  const handleUndoMarkPaid = async (reg) => {
    if (!window.confirm(
      `Undo manual payment for ${reg.player_name}? This reverts the status to Unpaid and clears the payment amount. The admin action will be audit-logged.`
    )) return;
    setUndoingId(reg.id);
    try {
      await api.post(`/tournaments/${tournamentId}/registrations/${reg.id}/undo-mark-paid`);
      toast.success(`Reverted ${reg.player_name} to Unpaid`);
      await fetchRegistrations?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || 'Undo failed');
      toast.error(msg);
    } finally {
      setUndoingId(null);
    }
  };

  const handleSyncStripeStatus = async (reg) => {
    setSyncingId(reg.id);
    try {
      const res = await api.post('/admin/payments/sync', {
        entity_type: 'tournament_registration',
        entity_id: reg.id,
      });
      const { outcome, hint, _updated, stripe_payment_intent_status, stripe_payment_status } = res.data || {};
      const detail = hint || `Stripe says: session=${stripe_payment_status || '—'}, PI=${stripe_payment_intent_status || '—'}`;
      if (outcome === 'succeeded') {
        toast.success(`Marked paid. ${detail}`);
      } else if (outcome === 'failed') {
        toast.warning(detail);  // already says "Marked as Unpaid…"
      } else if (outcome === 'processing') {
        toast.info(detail);
      } else {
        toast.message(detail);
      }
      fetchRegistrations && fetchRegistrations();
    } catch (err) {
      const errDetail = err.response?.data?.detail;
      // Structured 409 from backend when Stripe can't find the session.
      if (err.response?.status === 409 && errDetail && errDetail.code === 'stripe_session_missing') {
        const shouldClear = window.confirm(
          `${errDetail.message}\n\nSession ID: ${errDetail.session_id}\n\nClear this stuck session and reset the registration to unpaid? ` +
          `You'll be able to re-send a payment link or mark it paid offline.`
        );
        if (shouldClear) {
          try {
            const clearRes = await api.post('/admin/payments/sync', {
              entity_type: 'tournament_registration',
              entity_id: reg.id,
              clear_stale_session: true,
            });
            toast.success(clearRes.data?.hint || 'Stale Stripe session cleared.');
            fetchRegistrations && fetchRegistrations();
          } catch (clearErr) {
            toast.error(clearErr.response?.data?.detail || 'Failed to clear stale session');
          }
        }
      } else {
        const msg = typeof errDetail === 'string'
          ? errDetail
          : (errDetail?.message || 'Failed to sync with Stripe');
        toast.error(msg);
      }
    } finally {
      setSyncingId(null);
    }
  };

  const handleReinstate = async (reg) => {
    if (!window.confirm(`Reinstate ${reg.player_name}'s registration?`)) return;
    try {
      const res = await api.post(`/tournaments/${tournamentId}/registrations/${reg.id}/reinstate`);
      toast.success(`Registration reinstated as ${res.data.new_status.replace('_', ' ')}`);
      fetchRegistrations && fetchRegistrations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reinstate registration');
    }
  };
  const handleAdmitWaitlist = async (reg) => {
    if (!window.confirm(`Admit ${reg.player_name} from the waitlist? They'll be moved into the tournament and emailed a registration confirmation.`)) return;
    try {
      const res = await api.post(`/tournaments/${tournamentId}/registrations/${reg.id}/admit`);
      toast.success(`${reg.player_name} admitted (${(res.data.new_status || '').replace('_', ' ')})`);
      fetchRegistrations && fetchRegistrations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to admit from waitlist');
    }
  };
  const toggleSelectAll = () => {
    if (selectedRegistrations.length === filteredRegistrations.length) {
      setSelectedRegistrations([]);
    } else {
      setSelectedRegistrations(filteredRegistrations.map(r => r.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedRegistrations(prev => prev.includes(id) ? prev.filter(rid => rid !== id) : [...prev, id]);
  };

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex justify-between items-center">
          <CardTitle>Player Registrations</CardTitle>
          <div className="flex items-center gap-2">
            {selectedRegistrations.length > 0 && (
              <>
                {(isAdmin || isStaff || isCoach) && teams.length > 0 && (
                  <Button size="sm" variant="outline" onClick={onOpenBulkAddToTeam} data-testid="bulk-add-to-team-btn" className="text-blue-600 hover:text-blue-700">
                    <Users2 className="h-4 w-4 mr-1" />Add to Team ({selectedRegistrations.length})
                  </Button>
                )}
                {(isAdmin || isStaff) && (
                  <Button size="sm" variant="outline" onClick={onOpenBulkMarkPaid} data-testid="bulk-mark-paid-btn" className="text-green-600 hover:text-green-700">
                    <CreditCard className="h-4 w-4 mr-1" />Mark Paid ({selectedRegistrations.length})
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={onBulkPaymentReminder} data-testid="bulk-reminder-btn">
                  <Mail className="h-4 w-4 mr-1" />Send Reminder ({selectedRegistrations.length})
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 mt-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Age:</Label>
            <Select value={ageFilter} onValueChange={setAgeFilter}>
              <SelectTrigger className="w-28" data-testid="reg-age-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {tournament.age_categories?.map(cat => (<SelectItem key={cat} value={cat}>{cat.toUpperCase()}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Payment:</Label>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="w-32" data-testid="reg-payment-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Team:</Label>
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger className="w-36" data-testid="reg-team-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Players</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                <SelectItem value="assigned">Assigned to Team</SelectItem>
                {teams.map(team => (<SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Sort:</Label>
            <Select value={sortField} onValueChange={setSortField}>
              <SelectTrigger className="w-36" data-testid="reg-sort-field"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="player_name">Player Name</SelectItem>
                <SelectItem value="age_category">Age Group</SelectItem>
                <SelectItem value="team_name">Team</SelectItem>
                <SelectItem value="payment_status">Payment Status</SelectItem>
                <SelectItem value="registered_at">Registration Date</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')} data-testid="reg-sort-order">
              {sortOrder === 'asc' ? '\u2191 Asc' : '\u2193 Desc'}
            </Button>
          </div>
          {/* iter307ai — Toggle to surface archived (deleted/cancelled)
              registrations. Defaults off so the everyday roster view
              stays clean. */}
          {(isAdmin || isStaff) && setShowArchived && (
            <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
              <Checkbox
                id="reg-show-archived"
                checked={!!showArchived}
                onCheckedChange={(v) => setShowArchived(!!v)}
                data-testid="reg-show-archived-toggle"
              />
              <Label htmlFor="reg-show-archived" className="text-sm cursor-pointer select-none">
                Show archived
              </Label>
            </div>
          )}
          <div className="text-sm text-slate-500 ml-auto">Showing {filteredRegistrations.length} of {registrations.length}</div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredRegistrations.length === 0 ? (
          <div className="text-center py-8 text-slate-500">No registrations found</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"><Checkbox checked={selectedRegistrations.length === filteredRegistrations.length && filteredRegistrations.length > 0} onCheckedChange={toggleSelectAll} /></TableHead>
                <TableHead>Player</TableHead><TableHead>DOB</TableHead><TableHead>Parent</TableHead><TableHead>Category</TableHead>
                <TableHead>Team</TableHead><TableHead>Status</TableHead><TableHead>Payment</TableHead>
                <TableHead>Amount</TableHead><TableHead>Registered</TableHead><TableHead>Modified</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRegistrations.map(reg => (
                <TableRow key={reg.id} data-testid={`registration-row-${reg.id}`}>
                  <TableCell><Checkbox checked={selectedRegistrations.includes(reg.id)} onCheckedChange={() => toggleSelect(reg.id)} data-testid={`registration-checkbox-${reg.id}`} /></TableCell>
                  <TableCell>
                    <div>
                      <Link to={`/admin/players/${reg.player_id}`} className="font-medium text-blue-600 hover:text-blue-700 hover:underline" data-testid={`player-link-${reg.player_id}`}>{reg.player_name}</Link>
                      <p className="text-xs text-slate-500">{reg.can_travel ? 'Can Travel' : 'Local Only'}</p>
                    </div>
                  </TableCell>
                  <TableCell data-testid={`player-dob-${reg.id}`}>
                    {reg.date_of_birth ? (
                      <div className="text-sm">
                        <p className="font-medium text-slate-700">
                          {(() => {
                            // Render YYYY-MM-DD as a localized date without
                            // shifting timezones (treat as local midnight).
                            const [y, m, d] = reg.date_of_birth.split('-').map(Number);
                            if (!y || !m || !d) return reg.date_of_birth;
                            return new Date(y, m - 1, d).toLocaleDateString();
                          })()}
                        </p>
                        <p className="text-xs text-slate-500">
                          {(() => {
                            const [y, m, d] = reg.date_of_birth.split('-').map(Number);
                            if (!y || !m || !d) return '';
                            const today = new Date();
                            let age = today.getFullYear() - y;
                            const monthDiff = today.getMonth() + 1 - m;
                            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d)) age -= 1;
                            return `${age} yrs`;
                          })()}
                        </p>
                      </div>
                    ) : (
                      <span className="text-slate-400 text-xs italic">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{reg.parent_name}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">{reg.parent_email && (<span className="flex items-center gap-1"><Mail className="h-3 w-3" />{reg.parent_email}</span>)}</div>
                      {reg.parent_phone && (<span className="flex items-center gap-1 text-xs text-slate-500"><Phone className="h-3 w-3" />{reg.parent_phone}</span>)}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" data-testid={`registration-age-badge-${reg.id}`}>{reg.age_category ? reg.age_category.toUpperCase() : 'OPEN'}</Badge></TableCell>
                  <TableCell>{reg.team_name ? (<Badge variant="secondary" className="bg-blue-100 text-blue-800">{reg.team_name}</Badge>) : (<span className="text-slate-400 text-sm italic">Unassigned</span>)}</TableCell>
                  <TableCell>
                    {(() => {
                      // `payment_pending` is a legacy enum value that overlaps
                      // with the Payment column — a player in that state is
                      // functionally "selected" (on a team) and their Payment
                      // column already shows UNPAID. Display it as `selected`
                      // here so the Status column reflects player lifecycle
                      // only, not payment state.
                      const displayStatus = reg.status === 'payment_pending' ? 'selected' : reg.status;
                      return (
                        <div className="flex items-center gap-1 flex-wrap">
                          <Badge className={registrationStatusColors[displayStatus] || registrationStatusColors[reg.status]}>
                            {displayStatus.replace('_', ' ')}
                          </Badge>
                          {reg.is_archived && (
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-slate-100 text-slate-600 border-slate-300"
                              data-testid={`registration-archived-badge-${reg.id}`}
                              title={reg.archived_reason || 'Archived'}
                            >
                              Archived
                            </Badge>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      // A Stripe checkout that was initiated but never
                      // completed leaves the registration with either
                      // `status === 'payment_pending'` or a `stripe_session_id`
                      // set. Surface that distinctly ("PAYMENT PENDING", blue)
                      // instead of plain red "UNPAID" so admins can tell a
                      // half-finished payment apart from one never started.
                      const isInitiated =
                        !reg.payment_status || reg.payment_status !== 'paid'
                          ? (reg.status === 'payment_pending' || Boolean(reg.stripe_session_id))
                          : false;
                      const displayKey = reg.payment_status === 'paid'
                        ? 'paid'
                        : reg.payment_status === 'refunded'
                          ? 'refunded'
                          : reg.payment_status === 'partial'
                            ? 'partial'
                            : reg.payment_status === 'processing'
                              ? 'processing'
                              : isInitiated
                                ? 'payment_pending'
                                : (reg.payment_status || 'unpaid');
                      const displayLabel = displayKey === 'payment_pending'
                        ? 'PAYMENT PENDING'
                        : displayKey === 'processing'
                          ? 'PROCESSING'
                          : displayKey === 'partial'
                            ? 'PARTIAL'
                            : displayKey.toUpperCase();
                      return (
                        <div className="flex items-center gap-2">
                          {getPaymentStatusIcon(reg.payment_status)}
                          <Badge className={paymentStatusColors[displayKey] || paymentStatusColors.unpaid}>
                            {displayLabel}
                          </Badge>
                          {isInitiated && (
                            (RETIRED_TOOLS_VISIBLE ? (<Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={(e) => { e.stopPropagation(); handleSyncStripeStatus(reg); }}
                              disabled={syncingId === reg.id}
                              title="Refresh Stripe payment status"
                              data-testid={`refresh-stripe-${reg.id}`}
                            >
                              <RefreshCcw className={`h-3.5 w-3.5 ${syncingId === reg.id ? 'animate-spin' : ''}`} />
                            </Button>) : null)
                          )}
                        </div>
                      );
                    })()}
                    {reg.payment_method && reg.payment_status !== 'refunded' && (
                      <p className="text-xs text-slate-500 mt-1 capitalize" data-testid={`payment-method-label-${reg.id}`}>
                        {reg.payment_method}
                      </p>
                    )}
                    {reg.payment_status === 'refunded' && reg.last_refund_method && (
                      <p className="text-xs text-slate-500 mt-1 capitalize" data-testid={`refund-method-label-${reg.id}`}>
                        Refunded as {reg.last_refund_method === 'stripe' ? 'Stripe refund' : reg.last_refund_method === 'credit' ? 'account credit' : reg.last_refund_method}
                      </p>
                    )}
                    {reg.payment_status === 'partial' && (
                      <p
                        className="text-xs text-amber-700 mt-1"
                        data-testid={`partial-breakdown-${reg.id}`}
                      >
                        Credit applied: {cs()}{Number(reg.credit_applied || 0).toFixed(2)}
                        {' · '}Due: <strong>{cs()}{Number(reg.balance_due || 0).toFixed(2)}</strong>
                      </p>
                    )}
                  </TableCell>
                  <TableCell>{reg.payment_status === 'paid' ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <NetAmount
                        amount={reg.payment_amount}
                        refundedAmount={reg.refunded_amount}
                        refundStatus={reg.refund_status}
                        amountClassName="text-green-600 font-medium"
                        testId={`reg-net-${reg.id}`}
                      />
                      {/* Per-row Fix Amount: shows when paid but the amount
                          is missing/zero. One click tries Stripe; on failure
                          opens manual entry. */}
                      {(!reg.payment_amount || Number(reg.payment_amount) <= 0) && (
                        <RowAmountFixer
                          entityType="tournament_registration"
                          entityId={reg.id}
                          currentAmount={reg.payment_amount}
                          onUpdated={fetchRegistrations}
                          compact
                        />
                      )}
                    </div>
                  ) : (<span className="text-slate-500">{cs()}{tournament.initial_payment_amount}</span>)}</TableCell>
                  <TableCell><span className="text-sm text-slate-600">{reg.created_at ? new Date(reg.created_at).toLocaleDateString() : '-'}</span></TableCell>
                  <TableCell><span className="text-sm text-slate-600">{reg.updated_at ? new Date(reg.updated_at).toLocaleDateString() : '-'}</span></TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {reg.status !== 'cancelled' && (<DropdownMenuItem onClick={() => setEditReg(reg)} data-testid={`edit-registration-${reg.id}`}><Pencil className="h-4 w-4 mr-2" />Edit Team / Age</DropdownMenuItem>)}
                        {reg.status === 'cancelled' && (<DropdownMenuItem onClick={() => handleReinstate(reg)} data-testid={`reinstate-registration-${reg.id}`} className="text-green-600"><RotateCcw className="h-4 w-4 mr-2" />Reinstate Registration</DropdownMenuItem>)}
                        {reg.status === 'waitlisted' && (<DropdownMenuItem onClick={() => handleAdmitWaitlist(reg)} data-testid={`admit-waitlist-${reg.id}`} className="text-blue-600"><CheckCircle className="h-4 w-4 mr-2" />Admit from Waitlist</DropdownMenuItem>)}
                        {reg.status !== 'cancelled' && reg.payment_status !== 'paid' && (<DropdownMenuItem onClick={() => onOpenPaymentDialog(reg)}><CreditCard className="h-4 w-4 mr-2" />Mark as Paid</DropdownMenuItem>)}
                        {reg.status !== 'cancelled' && reg.payment_status !== 'paid' && (<DropdownMenuItem onClick={() => { api.post(`/tournaments/${tournamentId}/send-payment-link/${reg.id}`).then((r) => { if (r.data?.email_sent) { toast.success(r.data?.message || 'Payment link sent'); } else { toast.error(r.data?.message || 'Failed to send payment link'); } }).catch((err) => toast.error(err.response?.data?.detail || 'Failed to send payment link')); }} data-testid={`send-payment-link-${reg.id}`}><Send className="h-4 w-4 mr-2" />Send Payment Link</DropdownMenuItem>)}
                        {reg.status !== 'cancelled' && (<DropdownMenuItem onClick={() => onOpenAddToTeamDialog(reg)} data-testid={`add-to-team-${reg.id}`}><Users2 className="h-4 w-4 mr-2" />Add to Team</DropdownMenuItem>)}
                        <DropdownMenuItem onClick={() => setDiscountReg(reg)} data-testid={`discount-registration-${reg.id}`} className="text-emerald-700"><TicketPercent className="h-4 w-4 mr-2" />Apply Discount</DropdownMenuItem>
                        {reg.payment_status === 'paid' && isAdmin && (<DropdownMenuItem onClick={() => onOpenRefundDialog?.(reg)} data-testid={`refund-menu-${reg.id}`}><RefreshCw className="h-4 w-4 mr-2" />Issue Refund</DropdownMenuItem>)}
                        {reg.payment_status === 'paid'
                          && ['cash', 'check', 'venmo', 'zelle', 'bank_transfer', 'other', 'manual'].includes(String(reg.payment_method || '').toLowerCase())
                          && !reg.stripe_payment_intent_id
                          && !reg.stripe_session_id
                          && (
                            <DropdownMenuItem
                              onClick={() => handleUndoMarkPaid(reg)}
                              disabled={undoingId === reg.id}
                              data-testid={`undo-mark-paid-${reg.id}`}
                              className="text-amber-700"
                            >
                              <RotateCcw className="h-4 w-4 mr-2" />
                              {undoingId === reg.id ? 'Undoing…' : 'Undo Mark as Paid'}
                            </DropdownMenuItem>
                          )}
                        <DropdownMenuItem className="text-red-600" onClick={() => onOpenDeleteRegistrationDialog(reg)} data-testid={`delete-registration-${reg.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete Registration</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <ManualDiscountDialog
        open={!!discountReg}
        onOpenChange={(v) => !v && setDiscountReg(null)}
        transactionType="tournament_registration"
        transactionId={discountReg?.id}
        onChange={fetchRegistrations}
      />
      <EditRegistrationDialog
        open={!!editReg}
        onOpenChange={(v) => !v && setEditReg(null)}
        registration={editReg}
        tournament={tournament}
        teams={teams}
        onSaved={fetchRegistrations}
      />
    </Card>
  );
};
